import { DeleteOutlined } from '@ant-design/icons';
import { keepPreviousData,useInfiniteQuery } from '@tanstack/react-query';
import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  type KeyboardEvent,
  type RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';

import ScrollView from '@/components/Common/ScrollView';
import { MainText } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import { useInputs } from '@/hooks/pure/useInputs';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';
import { lowerCaseMatch } from '@/utils/util';

import { CenterFlex, Flex } from '../base';
import Spinner from '../Common/Spinner';
import Input from '../Inputs/Input';
import List from '../List/List';
import SelectPortal from './SelectPortal';
import S from './styled';
import type { FetcherFunc, SelectModalProps, SelectOptionT } from './types';

const SelectModal = (props: SelectModalProps) => {
  const { isEditor } = useUserRole();
  const { currDropdown, shortcutEnabled, setCurrDropdown } = useDropdownStore(
    ({ currDropdown, shortcutEnabled, setCurrDropdown }) => ({
      currDropdown,
      shortcutEnabled,
      setCurrDropdown,
    }),
  );

  const dropdownOpen = useMemo(() => {
    return currDropdown === props.name;
  }, [currDropdown, props.name]);

  const setDropdownOpenHandler = () => {
    isEditor && setCurrDropdown(dropdownOpen ? null : props.name || '');
  };

  useKeymap(props.shortcut, () => {
    shortcutEnabled && setDropdownOpenHandler();
  });

  const { dropdownPos, setDropdownRef } = useDropdownPos(dropdownOpen, {
    ref: props.anchorRef as RefObject<HTMLElement>,
    el: props.triggerEl,
    pos: props.pos,
    options: props.modalOptions,
  });

  return (
    <SelectPortal
      status={dropdownOpen}
      close={() => setCurrDropdown(null)}
      style={dropdownPos}
    >
      <Dropdown
        {...props}
        ref={setDropdownRef}
        width={props.width}
        close={() => setCurrDropdown(null)}
      />
    </SelectPortal>
  );
};

const Dropdown = forwardRef(
  (
    props: SelectModalProps & { close: () => void },
    ref?: ForwardedRef<HTMLDivElement>,
  ) => {
    const color = useColor();
    const [state, onChange, onCustomChange] = useInputs({
      searchValue: '',
    });

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
      useInfiniteQuery({
        queryKey: [props.name, state.searchValue],
        queryFn: Array.isArray(props.data)
          ? ({ pageParam }) => {
              const list = props.data as SelectOptionT[];
              return {
                list: list,
                cursor: pageParam,
              };
            }
          : ({ pageParam }) => {
              const fetcher = props.data as FetcherFunc<SelectOptionT>;
              return fetcher({
                cursor: pageParam ?? undefined,
                searchValue: state.searchValue,
              });
            },
        initialPageParam: null as string | null,
        getNextPageParam: (lastPage) => lastPage.cursor,
        placeholderData: keepPreviousData,
      });

    const dataList: SelectOptionT[] = useMemo(() => {
      return data?.pages.flatMap((page) => page.list) || [];
    }, [data?.pages]);

    const currList = useMemo(() => {
      return dataList.filter((option) => {
        return lowerCaseMatch(option.searchName, state.searchValue);
      });
    }, [dataList, state.searchValue]);

    const [init, setInit] = useState(false);

    const [focused, setFocused] = useState<SelectOptionT | null>(null);
    const [newFocused, setNewFocused] = useState<boolean>(false);

    useEffect(() => {
      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === -1 && currList.length) {
        setFocused(currList[0]);
      }
    }, [currList, focused?.value, state.searchValue]);

    const newItemCondition = useMemo(() => {
      return (
        state.searchValue.trim() !== '' &&
        props.onCreate &&
        !currList.find((option) => option.searchName === state.searchValue)
      );
    }, [state.searchValue, props.onCreate, currList]);

    const customItemCondition = useMemo(() => {
      return (
        state.searchValue.trim() !== '' &&
        props.customSearchOptions &&
        !currList.find((option) => option.searchName === state.searchValue)
      );
    }, [currList, props.customSearchOptions, state.searchValue]);

    const onSearchInputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
      if (props.dynamicChangeHandler) {
        props.dynamicChangeHandler(e.target.value);
      } else {
        const tempCurr = currList.filter((option) => {
          return lowerCaseMatch(option.searchName, e.target.value);
        });
        if (tempCurr.length > 0) {
          setFocused(currList[0]);
          setNewFocused(false);
        } else {
          setFocused(null);
          setNewFocused(true);
        }
      }
      onChange(e);
    };

    const onKeyEnter = () => {
      if (newFocused) {
        props.onCreate?.(state.searchValue);
        props.close();
        return;
      }
      if (!focused) return;
      props.onSelect?.(focused.value, props.name);
      focused.onClick?.(focused.value);
      props.close();
    };

    const onSearchInputKeyDown = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.code === 'ArrowUp') {
        e.preventDefault();
        focusPrevItem();
      }
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        focusNextItem();
      }
      if (e.code === 'Enter') {
        onKeyEnter();
      }
      if (e.code === 'Escape') {
        props.close?.();
      }
    };

    const focusNextItem = () => {
      if (currList.length > 0) {
        if (newFocused) {
          setFocused(currList[0]);
          setNewFocused(false);
          return;
        }
      }

      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === currList.length - 1) return;
      setFocused(currList[curr + 1]);
    };

    const focusPrevItem = () => {
      const curr = currList.findIndex(
        (option) => option.value === focused?.value,
      );
      if (curr === 0) {
        if (newItemCondition) {
          setFocused(null);
          setNewFocused(true);
          return;
        }
        return;
      }
      setFocused(currList[curr - 1]);
    };

    useEffect(() => {
      onCustomChange('searchValue', '');
      setNewFocused(false);
      setInit(false);
    }, [onCustomChange]);

    useEffect(() => {
      if (!init) {
        if (props.value) {
          setFocused(
            currList.find((option) => option.value === props.value) ||
              currList[0],
          );
        } else {
          const curr = currList.findIndex(
            (option) => option.value === focused?.value,
          );
          if (curr === -1 && currList.length > 0) {
            setFocused(currList[0]);
          }
        }

        setInit(true);
      }
    }, [init, focused?.value, state.searchValue, currList, props.value]);

    useKeymap('ArrowUp', focusPrevItem);

    useKeymap('ArrowDown', focusNextItem);

    useKeymap('Enter', () => {
      onKeyEnter();
    });
    return (
      <S.OptionList width={props.width} ref={ref}>
        {props.searchOptions?.enable && (
          <Input
            style={{ width: '100%', height: 32 }}
            placeholder={props.searchOptions.placeholder || 'Search...'}
            name="searchValue"
            sizeType="m"
            value={state.searchValue}
            onChange={onSearchInputChangeHandler}
            onKeyDown={onSearchInputKeyDown}
          />
        )}

        <ScrollView style={{ maxHeight: 300 }}>
          <Flex d="column" g="4px">
            {newItemCondition && (
              <S.NewItem
                d="row"
                a="center"
                g="5px"
                j="space-between"
                onClick={() => props.onCreate?.(state.searchValue)}
                focused={newFocused}
              >
                <Typo
                  style={{ userSelect: 'none', flex: 1 }}
                  weight="r"
                  type="body3"
                  color={color.gray700}
                  multilineEllipsis={1}
                >
                  {state.searchValue}
                </Typo>
                <Typo
                  style={{ userSelect: 'none' }}
                  weight="r"
                  type="body3"
                  color={color.primary100}
                >
                  {' new'}
                </Typo>
              </S.NewItem>
            )}
            {customItemCondition && (
              <S.NewItem
                d="row"
                a="center"
                g="5px"
                j="space-between"
                onClick={() => {
                  props.customSearchOptions?.onSelectCustomItem?.(
                    state.searchValue,
                  );
                  props.close?.();
                }}
                focused={newFocused}
              >
                <Typo
                  style={{ userSelect: 'none', flex: 1 }}
                  weight="r"
                  type="body3"
                  color={color.gray700}
                  multilineEllipsis={1}
                >
                  {props.customSearchOptions?.CustomItemNode(state.searchValue)}
                </Typo>
              </S.NewItem>
            )}
            {props.onClear && (
              <S.Option
                focused={false}
                onClick={(e) => {
                  e.preventDefault();
                  props.onClear?.();
                  props.close?.();
                }}
              >
                {props.placeholder}
              </S.Option>
            )}
            <List
              className="gap-0.5"
              data={currList}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isLoading={isLoading}
              isFetchingNextPage={isFetchingNextPage}
              loader={
                <CenterFlex height="200px">
                  <Spinner />
                </CenterFlex>
              }
              placeholder={{
                node: (
                  <CenterFlex height="200px">
                    <MainText dim>No Data</MainText>
                  </CenterFlex>
                ),
              }}
              render={(op, index) => (
                <S.Option
                  className="group"
                  autoFocus={
                    !props.searchOptions?.enable && focused?.value === op.value
                  }
                  focused={focused?.value === op.value}
                  key={op.value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onSelect?.(op.value, props.name);
                    op.onClick?.(op.value, e);
                    props.close();
                  }}
                >
                  {props.mapper?.(op, index) || op.searchName}
                  {props.onDelete && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        props.onDelete?.(op);
                      }}
                      className="flex items-center justify-center transition-opacity duration-200 opacity-0 group-hover:opacity-100 absolute right-4"
                    >
                      <DeleteOutlined style={{ fontSize: 14 }} />
                    </div>
                  )}
                </S.Option>
              )}
            />
          </Flex>
        </ScrollView>
      </S.OptionList>
    );
  },
);
export default SelectModal;
